import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  StickyNote2 as NoteIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Announcement as AnnouncementIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  PriorityHigh as HighPriorityIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { Note, NoteType, NotePriority } from '../../utils/types';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import { completeNote, uncompleteNote, archiveNote } from '../../services/noteService';

interface NotesListProps {
  notes: Note[];
  currentUserId: string;
}

const NotesList: React.FC<NotesListProps> = ({ notes, currentUserId }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  // تصفية الملاحظات بناءً على البحث
  const filteredNotes = notes.filter(note =>
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // ترتيب الملاحظات (غير المكتملة أولاً، ثم حسب الأولوية والتاريخ)
  const sortedNotes = filteredNotes.sort((a, b) => {
    // الملاحظات غير المكتملة أولاً
    if (a.isCompleted !== b.isCompleted) {
      return a.isCompleted ? 1 : -1;
    }
    
    // ترتيب حسب الأولوية
    const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
    const aPriority = priorityOrder[a.priority];
    const bPriority = priorityOrder[b.priority];
    
    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }
    
    // ترتيب حسب التاريخ (الأحدث أولاً)
    return b.updatedAt.getTime() - a.updatedAt.getTime();
  });

  // الحصول على أيقونة نوع الملاحظة
  const getNoteTypeIcon = (type: NoteType) => {
    switch (type) {
      case 'personal':
        return <PersonIcon />;
      case 'shared':
        return <GroupIcon />;
      case 'announcement':
        return <AnnouncementIcon />;
      default:
        return <NoteIcon />;
    }
  };

  // الحصول على لون نوع الملاحظة
  const getNoteTypeColor = (type: NoteType) => {
    switch (type) {
      case 'personal':
        return theme.palette.primary.main;
      case 'shared':
        return theme.palette.secondary.main;
      case 'announcement':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // الحصول على لون الأولوية
  const getPriorityColor = (priority: NotePriority) => {
    switch (priority) {
      case 'urgent':
        return theme.palette.error.main;
      case 'high':
        return theme.palette.warning.main;
      case 'medium':
        return theme.palette.info.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // الحصول على نص الأولوية
  const getPriorityText = (priority: NotePriority) => {
    switch (priority) {
      case 'urgent':
        return 'عاجل';
      case 'high':
        return 'عالي';
      case 'medium':
        return 'متوسط';
      case 'low':
        return 'منخفض';
      default:
        return '';
    }
  };

  // تنسيق الوقت
  const formatTime = (date: Date) => {
    try {
      return formatDistanceToNow(date, { addSuffix: true, locale: ar });
    } catch (error) {
      return 'الآن';
    }
  };

  // فتح قائمة الخيارات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, note: Note) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedNote(note);
  };

  // إغلاق قائمة الخيارات
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNote(null);
  };

  // تمييز الملاحظة كمكتملة/غير مكتملة
  const handleToggleComplete = async () => {
    if (!selectedNote) return;

    try {
      if (selectedNote.isCompleted) {
        await uncompleteNote(selectedNote.id);
      } else {
        await completeNote(selectedNote.id, currentUserId);
      }
    } catch (error) {
      console.error('Error toggling note completion:', error);
    }
    
    handleMenuClose();
  };

  // أرشفة الملاحظة
  const handleArchiveNote = async () => {
    if (!selectedNote) return;

    try {
      await archiveNote(selectedNote.id);
    } catch (error) {
      console.error('Error archiving note:', error);
    }
    
    handleMenuClose();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* شريط البحث */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="البحث في الملاحظات..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
      </Box>

      {/* قائمة الملاحظات */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {sortedNotes.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'لا توجد نتائج للبحث' : 'لا توجد ملاحظات'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {sortedNotes.map((note, index) => (
              <React.Fragment key={note.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    sx={{
                      py: 2,
                      px: 2,
                      opacity: note.isCompleted ? 0.6 : 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.1),
                      }
                    }}
                  >
                    {/* أيقونة الحالة */}
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNote(note);
                          handleToggleComplete();
                        }}
                      >
                        {note.isCompleted ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <UncheckedIcon color="action" />
                        )}
                      </IconButton>
                    </ListItemIcon>

                    {/* محتوى الملاحظة */}
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{
                              textDecoration: note.isCompleted ? 'line-through' : 'none',
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {note.title}
                          </Typography>

                          {/* شارات الأولوية والنوع */}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {note.priority === 'urgent' && (
                              <HighPriorityIcon 
                                sx={{ 
                                  fontSize: 16, 
                                  color: getPriorityColor(note.priority) 
                                }} 
                              />
                            )}
                            
                            <Avatar
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: getNoteTypeColor(note.type),
                                fontSize: '0.7rem'
                              }}
                            >
                              {getNoteTypeIcon(note.type)}
                            </Avatar>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box>
                          {/* محتوى الملاحظة */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mb: 1
                            }}
                          >
                            {note.content}
                          </Typography>

                          {/* معلومات إضافية */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* الأولوية */}
                              <Chip
                                size="small"
                                label={getPriorityText(note.priority)}
                                sx={{
                                  height: 18,
                                  fontSize: '0.7rem',
                                  bgcolor: alpha(getPriorityColor(note.priority), 0.1),
                                  color: getPriorityColor(note.priority)
                                }}
                              />

                              {/* تاريخ الاستحقاق */}
                              {note.dueDate && !note.isCompleted && (
                                <Chip
                                  size="small"
                                  icon={<ScheduleIcon sx={{ fontSize: 12 }} />}
                                  label={formatTime(note.dueDate)}
                                  sx={{
                                    height: 18,
                                    fontSize: '0.7rem',
                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                    color: theme.palette.warning.main
                                  }}
                                />
                              )}
                            </Box>

                            {/* معلومات المؤلف والوقت */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {note.authorId === currentUserId ? 'أنت' : note.authorName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                •
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatTime(note.updatedAt)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* العلامات */}
                          {note.tags.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                              {note.tags.slice(0, 3).map((tag, tagIndex) => (
                                <Chip
                                  key={tagIndex}
                                  size="small"
                                  label={tag}
                                  variant="outlined"
                                  sx={{
                                    height: 16,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-label': { px: 0.5 }
                                  }}
                                />
                              ))}
                              {note.tags.length > 3 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{note.tags.length - 3}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                    />

                    {/* زر الخيارات */}
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, note)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                </ListItem>
                
                {index < sortedNotes.length - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* قائمة الخيارات */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleToggleComplete}>
          <Typography variant="body2">
            {selectedNote?.isCompleted ? 'تمييز كغير مكتمل' : 'تمييز كمكتمل'}
          </Typography>
        </MenuItem>
        
        <MenuItem onClick={handleMenuClose}>
          <Typography variant="body2">تعديل</Typography>
        </MenuItem>
        
        <MenuItem onClick={handleMenuClose}>
          <Typography variant="body2">مشاركة</Typography>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleArchiveNote}>
          <Typography variant="body2">أرشفة</Typography>
        </MenuItem>
        
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <Typography variant="body2">حذف</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NotesList;
